import React from 'react';
import { Menu } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  UserOutlined,
  HourglassOutlined,
  HomeOutlined,
  DollarOutlined,
  ProfileOutlined,
  WalletOutlined,
  AuditOutlined,
  LockOutlined,
  ContactsOutlined,
  FileTextOutlined,
  BankOutlined,
} from '@ant-design/icons';

import { IState } from './redux/reducers';

import { Scope } from 'redux/reducers/authReducer';
import { usePermission } from 'hooks/usePermission';
import { useIsReadOnly, useScreenSize } from 'hooks';

interface RenderMenuItemProps {
  key: string;
  title: string;
  icon: React.ReactNode;
  subscriptionRequired?: boolean;
  hasPermission?: boolean;
}

interface AppMenuProps {
  setCollapsed: (value: boolean) => void;
}

export function AppMenu(props: AppMenuProps) {
  const isSmallScreen = useScreenSize();
  const history = useHistory();
  const location = useLocation();

  const isReadOnly = useIsReadOnly();

  const authState = useSelector((state: IState) => state.auth);

  const hasPermissionContactsRead = usePermission(Scope.ContactsRead);
  const hasPermissionAgreementsRead = usePermission(Scope.AgreementsRead);
  const hasPermissionRatesRead = usePermission(Scope.RatesRead);
  const hasPermissionShiftsRead = usePermission(Scope.ShiftsRead);
  const hasPermissionInvoicesRead = usePermission(Scope.InvoicesRead);
  const hasPermissionExpensesRead = usePermission(Scope.ExpensesRead);
  const hasPermissionReportsShift = usePermission(Scope.ReportsShift);
  const hasPermissionReportsTax = usePermission(Scope.ReportsTax);
  const hasPermissionReportsInvoice = usePermission(Scope.ReportsInvoice);

  const onMenuSelected = (key: string | number) => {
    if (isSmallScreen && props.setCollapsed) {
      props.setCollapsed(true);
    }
    history.push(`/${key}`);
  };

  let path = location.pathname.replace(/\//g, '');
  if (path === '') {
    path = 'dashboard';
  }

  const renderMenuItem = (options: RenderMenuItemProps) => {
    let disabled = false;
    if (options.hasPermission === false) {
      return <></>;
    } else if (options.subscriptionRequired && isReadOnly) {
      disabled = true;
    }

    return (
      <Menu.Item key={options.key} disabled={disabled} className={disabled ? 'read-only' : ''} icon={disabled ? <LockOutlined /> : options.icon}>
        {options.title}
      </Menu.Item>
    );
  };

  const renderAdminMenuItem = (options: RenderMenuItemProps) => {
    if (!authState.currentUser.isAdmin) {
      return <></>;
    }
    return renderMenuItem(options);
  };

  return (
    <Menu theme="dark" mode="inline" selectedKeys={[path]} onClick={({ key }) => onMenuSelected(key)}>
      {renderMenuItem({ key: 'dashboard', title: 'Dashboard', icon: <HomeOutlined /> })}
      {renderMenuItem({ key: 'contacts', title: 'Contacts', icon: <ContactsOutlined />, hasPermission: hasPermissionContactsRead })}
      {renderMenuItem({ key: 'agreements', title: 'Service agreements', icon: <AuditOutlined />, hasPermission: hasPermissionAgreementsRead })}
      {renderMenuItem({ key: 'rates', title: 'Rates', icon: <DollarOutlined />, subscriptionRequired: true, hasPermission: hasPermissionRatesRead })}
      {renderMenuItem({ key: 'shifts', title: 'Shifts', icon: <HourglassOutlined />, subscriptionRequired: true, hasPermission: hasPermissionShiftsRead })}
      {renderMenuItem({ key: 'invoices', title: 'Invoices', icon: <ProfileOutlined />, subscriptionRequired: true, hasPermission: hasPermissionInvoicesRead })}
      {renderMenuItem({ key: 'expenses', title: 'Expenses', icon: <WalletOutlined />, subscriptionRequired: true, hasPermission: hasPermissionExpensesRead })}
      <Menu.SubMenu key="reports" title="Reports" icon={<FileTextOutlined />}>
        {renderMenuItem({ key: 'reports/shift', title: 'Shift Report', icon: <FileTextOutlined />, subscriptionRequired: true, hasPermission: hasPermissionReportsShift })}
        {renderMenuItem({ key: 'reports/tax', title: 'Tax Report', icon: <FileTextOutlined />, subscriptionRequired: true, hasPermission: hasPermissionReportsTax })}
        {renderMenuItem({ key: 'reports/invoice', title: 'Invoice Report', icon: <FileTextOutlined />, subscriptionRequired: true, hasPermission: hasPermissionReportsInvoice })}
      </Menu.SubMenu>
      {renderAdminMenuItem({ key: 'users', title: 'Users', icon: <UserOutlined /> })}
      {renderAdminMenuItem({ key: 'organizations', title: 'Organizations', icon: <BankOutlined /> })}
    </Menu>
  );
}
